/**
 * CompareReportsModal — Iter270 Phase D (COACHDIA Pulse)
 * =======================================================
 *
 * Vue comparative RÉELLE v1 → v2 sur rapports générés (ZÉRO simulation).
 *
 * Principe produit :
 *   « Montrer l'impact réel d'un humain sur un système. »
 *
 * Affichage :
 *   HERO espresso + verdict narratif dérivé déterministement des deltas
 *   DELTAS : Balance / Dominante / Distribution 4 énergies (avant/après)
 *   BLOC DÉCISIONNEL "Ce que cela change pour vous, en tant que manager"
 *     → 3 impacts concrets + 1 risque + 1 recommandation (calculés sans LLM)
 *
 * Source de vérité : 2 rapports RÉELS (baselineBatch.report_data + currentBatch.report_data)
 * Fallback : si `newcomer_impact` déjà persisté sur currentBatch → utilisé.
 */
import React from 'react';
import { X, TrendingUp, TrendingDown, Minus, AlertTriangle, Lightbulb, FileText } from 'lucide-react';
import { getApiUrl } from '../../config/environment';

const API = getApiUrl();
const authHeaders = () => {
  const t = localStorage.getItem('token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const COLOR_TOKENS = {
  red:    { label: 'Action',    hex: '#B94A48' },
  yellow: { label: 'Influence', hex: '#C8A64B' },
  green:  { label: 'Harmonie',  hex: '#5B8A6B' },
  blue:   { label: 'Analyse',   hex: '#3B6B8F' },
};

const fmtDelta = (v) => {
  if (v == null || isNaN(v)) return '—';
  const s = v.toFixed(1);
  return v > 0 ? `+${s}` : s;
};

/* ===========================================================================
 * Déterministe insights engine — pure JS, LLM-free
 * ========================================================================== */
function buildDecisionInsights({ before, after, newcomerName }) {
  const impacts = [];
  let risk = null;
  let reco = null;

  const bDist = before?.distribution || {};
  const aDist = after?.distribution || {};
  const balanceDelta = (after?.balance_score ?? 0) - (before?.balance_score ?? 0);
  const domBefore = before?.team_dominant;
  const domAfter = after?.team_dominant;

  // Impact 1 : cohésion
  if (Math.abs(balanceDelta) >= 3) {
    if (balanceDelta > 0) {
      impacts.push({
        kind: 'up',
        text: `Cohésion renforcée : le score d'équilibre progresse de ${fmtDelta(balanceDelta)} points, signalant une équipe plus harmonique.`,
      });
    } else {
      impacts.push({
        kind: 'down',
        text: `Cohésion fragilisée : le score d'équilibre recule de ${fmtDelta(balanceDelta)} points — l'arrivée introduit une tension mesurable.`,
      });
    }
  } else {
    impacts.push({
      kind: 'flat',
      text: `Équilibre préservé : le score reste stable (${fmtDelta(balanceDelta)}), l'intégration n'altère pas la dynamique générale.`,
    });
  }

  // Impact 2 : énergie la plus amplifiée et la plus affaiblie
  const diffs = ['red', 'yellow', 'green', 'blue'].map((k) => ({
    k, delta: (aDist[k] ?? 0) - (bDist[k] ?? 0),
  })).sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));
  const topShift = diffs[0];
  if (topShift && Math.abs(topShift.delta) >= 3) {
    const lbl = COLOR_TOKENS[topShift.k]?.label || topShift.k;
    if (topShift.delta > 0) {
      impacts.push({
        kind: 'up',
        text: `Énergie "${lbl}" renforcée de ${fmtDelta(topShift.delta)} pts — l'équipe gagne ${topShift.k === 'red' ? "en capacité d'exécution"
          : topShift.k === 'yellow' ? "en puissance relationnelle"
          : topShift.k === 'green' ? "en stabilité harmonique"
          : "en profondeur analytique"}.`,
      });
    } else {
      impacts.push({
        kind: 'down',
        text: `Énergie "${lbl}" affaiblie de ${fmtDelta(topShift.delta)} pts — cette dimension mérite un relais avant un prochain recrutement.`,
      });
    }
  }

  // Impact 3 : dominante
  if (domBefore && domAfter && domBefore !== domAfter) {
    const lblBefore = COLOR_TOKENS[domBefore]?.label || domBefore;
    const lblAfter = COLOR_TOKENS[domAfter]?.label || domAfter;
    impacts.push({
      kind: 'shift',
      text: `Bascule stratégique : la dominante d'équipe passe de "${lblBefore}" à "${lblAfter}". Votre positionnement identitaire évolue.`,
    });
  } else if (domAfter) {
    const lbl = COLOR_TOKENS[domAfter]?.label || domAfter;
    const beforeVal = bDist[domAfter] ?? 0;
    const afterVal = aDist[domAfter] ?? 0;
    const reinforced = afterVal > beforeVal + 2;
    if (reinforced) {
      impacts.push({
        kind: 'up',
        text: `La dominante "${lbl}" s'intensifie (+${(afterVal - beforeVal).toFixed(1)} pts) — l'équipe affirme davantage son identité existante.`,
      });
    }
  }

  // Risque : si une énergie tombe sous 20 %
  const weakest = ['red', 'yellow', 'green', 'blue']
    .map((k) => ({ k, v: aDist[k] ?? 0 }))
    .sort((a, b) => a.v - b.v)[0];
  if (weakest && weakest.v > 0 && weakest.v < 20) {
    const lbl = COLOR_TOKENS[weakest.k]?.label || weakest.k;
    risk = `Sous-représentation de l'énergie "${lbl}" (${weakest.v.toFixed(1)} %). À un effectif réduit, cette dimension devient mécaniquement absente des arbitrages collectifs.`;
  } else if (balanceDelta < -5) {
    risk = `L'arrivée fragilise l'équilibre global de l'équipe. Un troisième recrutement non complémentaire accentuerait la polarisation.`;
  } else {
    risk = `Homogénéité croissante : l'intégration n'apporte pas de contraste fort. Vigilance sur le risque de pensée-convergente lors des décisions complexes.`;
  }

  // Recommandation
  if (balanceDelta > 3) {
    reco = `Profil à modéliser : ${newcomerName} consolide l'équipe. Utilisez-le comme référence pour vos prochains recrutements afin de maintenir cette dynamique.`;
  } else if (balanceDelta < -3) {
    reco = `Compenser : lors du prochain recrutement, privilégiez un profil portant les énergies affaiblies ci-dessus pour restaurer l'équilibre mesuré en v1.`;
  } else {
    const strongestAfter = diffs.filter((d) => d.delta < 0)[0];
    if (strongestAfter) {
      const lbl = COLOR_TOKENS[strongestAfter.k]?.label || strongestAfter.k;
      reco = `Prochain recrutement orienté "${lbl}" recommandé pour maintenir la complétude énergétique de l'équipe.`;
    } else {
      reco = `Maintenir la composition actuelle — l'intégration s'inscrit dans la continuité sans déséquilibre mesurable.`;
    }
  }

  return { impacts: impacts.slice(0, 3), risk, reco };
}

/* ===========================================================================
 * Distribution delta bar
 * ========================================================================== */
const DistBar = ({ label, color, before, after }) => {
  const delta = (after ?? 0) - (before ?? 0);
  const isUp = delta > 0.5;
  const isDown = delta < -0.5;
  return (
    <div className="space-y-1" data-testid={`compare-dist-${label.toLowerCase()}`}>
      <div className="flex justify-between items-center text-[11px]">
        <span className="uppercase tracking-wider font-semibold" style={{ color }}>{label}</span>
        <span className={`tabular-nums font-semibold ${isUp ? 'text-[#5B8A6B]' : isDown ? 'text-[#B94A48]' : 'text-[#6B5E48]'}`}>
          {fmtDelta(delta)}
        </span>
      </div>
      <div className="relative h-[10px] rounded-full bg-[#E5DFD0] overflow-hidden">
        <div className="absolute inset-y-0 left-0 rounded-full opacity-40"
             style={{ width: `${Math.min(100, before ?? 0)}%`, background: color }} />
        <div className="absolute inset-y-0 left-0 rounded-full"
             style={{ width: `${Math.min(100, after ?? 0)}%`, background: color }} />
      </div>
      <div className="flex justify-between text-[10px] text-[#6B5E48] tabular-nums">
        <span>Avant <strong>{(before ?? 0).toFixed(1)}%</strong></span>
        <span>Après <strong>{(after ?? 0).toFixed(1)}%</strong></span>
      </div>
    </div>
  );
};

/* ===========================================================================
 * Main modal
 * ========================================================================== */
export default function CompareReportsModal({ open, onClose, currentBatch, baselineBatch }) {
  if (!open || !currentBatch || !baselineBatch) return null;

  const before = baselineBatch.report_data || {};
  const after = currentBatch.report_data || {};

  // Impact persisté si dispo (newcomer_impact calculé backend), sinon fallback local
  const impactObj = currentBatch.newcomer_impact;
  const newcomerName = impactObj?.newcomer?.name || guessNewcomerName(baselineBatch, currentBatch);

  const balanceBefore = before.balance_score ?? 0;
  const balanceAfter = after.balance_score ?? 0;
  const balanceDelta = balanceAfter - balanceBefore;
  const domBefore = before.team_dominant;
  const domAfter = after.team_dominant;
  const domShifted = domBefore && domAfter && domBefore !== domAfter;

  const { impacts, risk, reco } = buildDecisionInsights({ before, after, newcomerName });

  // Verdict éditorial (MAJUSCULES espresso-like)
  let verdict;
  if (domShifted) {
    verdict = `BASCULE STRATÉGIQUE · DOMINANTE ${COLOR_TOKENS[domAfter]?.label?.toUpperCase() || '?'}`;
  } else if (balanceDelta > 3) {
    verdict = `COHÉSION RENFORCÉE · ÉQUIPE CONSOLIDÉE`;
  } else if (balanceDelta < -3) {
    verdict = `ÉQUILIBRE FRAGILISÉ · ARBITRAGES À PRÉVOIR`;
  } else if (domAfter) {
    verdict = `DOMINANTE ${COLOR_TOKENS[domAfter]?.label?.toUpperCase() || '?'} INTENSIFIÉE`;
  } else {
    verdict = `INTÉGRATION NEUTRE · CONTINUITÉ`;
  }

  const downloadPdf = async () => {
    try {
      const r = await fetch(`${API}/team-pulse/batches/${currentBatch.id}/report/pdf`, {
        headers: authHeaders(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${currentBatch.name || 'rapport'}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      // Silent fail — le bouton "Ouvrir rapport complet" reste disponible
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" data-testid="pulse-compare-modal">
      <div className="bg-[#F5F1EA] max-w-4xl w-full max-h-[94vh] overflow-y-auto rounded-2xl shadow-2xl">
        {/* =========== HERO ESPRESSO =========== */}
        <section className="bg-[#1F1A12] text-white p-8 relative overflow-hidden rounded-t-2xl">
          <div className="absolute inset-0 opacity-[0.1]" style={{
            backgroundImage: `radial-gradient(circle at 20% 80%, ${COLOR_TOKENS[domAfter]?.hex || '#F3D060'} 0, transparent 45%)`,
          }} />
          <div className="relative">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] tracking-[0.4em] text-[#F3D060] uppercase font-semibold">
                  COACHDIA · PULSE · Impact mesuré
                </div>
                <div className="w-12 h-[2px] bg-[#F3D060] my-4" />
                <h2 className="font-serif text-3xl lg:text-[36px] font-medium leading-snug">
                  Impact de <span className="italic">{newcomerName}</span> sur l'équipe.
                </h2>
                <div className="text-[13px] tracking-wide text-white/90 mt-4 uppercase font-semibold tabular-nums" data-testid="compare-verdict">
                  {verdict}
                </div>
              </div>
              <button onClick={onClose} className="text-white/70 hover:text-white transition shrink-0" data-testid="pulse-compare-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* KPI pills : balance + dominante */}
            <div className="grid grid-cols-2 gap-4 mt-8 pt-6 border-t border-white/10">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFC098] font-semibold">
                  Score d'équilibre
                </div>
                <div className="mt-2 flex items-baseline gap-3 tabular-nums">
                  <span className="font-serif text-xl text-white/60">{balanceBefore.toFixed(1)}</span>
                  <span className="text-[#F3D060]">→</span>
                  <span className="font-serif text-3xl text-[#F3D060]">{balanceAfter.toFixed(1)}</span>
                  <span className={`text-sm font-semibold ${balanceDelta > 0 ? 'text-[#B7D4BE]' : balanceDelta < 0 ? 'text-[#F5A8A6]' : 'text-[#CFC098]'}`}>
                    ({fmtDelta(balanceDelta)})
                  </span>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#CFC098] font-semibold">
                  Dominante
                </div>
                <div className="mt-2 flex items-baseline gap-3">
                  {domBefore && (
                    <span className="font-serif text-xl" style={{ color: COLOR_TOKENS[domBefore]?.hex, opacity: 0.65 }}>
                      {COLOR_TOKENS[domBefore]?.label || domBefore}
                    </span>
                  )}
                  <span className="text-[#F3D060]">→</span>
                  <span className="font-serif text-2xl font-medium" style={{ color: COLOR_TOKENS[domAfter]?.hex }}>
                    {COLOR_TOKENS[domAfter]?.label || domAfter || '—'}
                  </span>
                  {domShifted && (
                    <span className="text-xs text-[#F3D060] uppercase tracking-wider font-semibold">Bascule</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </section>

        <div className="p-8 space-y-10">
          {/* =========== DISTRIBUTION =========== */}
          <section data-testid="compare-distribution">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-2">Section I</div>
            <h3 className="font-serif text-2xl mb-5 border-b border-[#E5DFD0] pb-3">
              Distribution des énergies
            </h3>
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-5">
              {Object.entries(COLOR_TOKENS).map(([k, v]) => (
                <DistBar key={k} label={v.label} color={v.hex}
                         before={before.distribution?.[k]} after={after.distribution?.[k]} />
              ))}
            </div>
          </section>

          {/* =========== INSIGHTS DÉCISIONNELS =========== */}
          <section className="bg-[#F3D060]/10 border border-[#F3D060]/40 rounded-2xl p-7" data-testid="compare-manager-insights">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#92710A] font-semibold mb-1">
              Lecture décisionnelle
            </div>
            <h3 className="font-serif text-2xl mb-5">
              Ce que cela change pour vous, en tant que manager.
            </h3>

            <div className="space-y-4">
              {impacts.map((impact, i) => {
                const Icon = impact.kind === 'up' ? TrendingUp
                  : impact.kind === 'down' ? TrendingDown : Minus;
                const color = impact.kind === 'up' ? '#5B8A6B'
                  : impact.kind === 'down' ? '#B94A48' : '#92710A';
                return (
                  <div key={i} className="flex gap-4 items-start">
                    <div className="shrink-0 w-9 h-9 rounded-full bg-white flex items-center justify-center border border-[#E5DFD0]">
                      <Icon className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 pt-1">
                      <div className="text-[10px] uppercase tracking-wider text-[#92710A] font-semibold mb-0.5">
                        Impact {i + 1}
                      </div>
                      <p className="text-[15px] text-[#1F1A12] leading-relaxed">{impact.text}</p>
                    </div>
                  </div>
                );
              })}

              {risk && (
                <div className="flex gap-4 items-start pt-4 border-t border-[#E5DFD0]/60" data-testid="compare-risk">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#B94A48] flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#B94A48] font-semibold mb-0.5">
                      Risque à surveiller
                    </div>
                    <p className="text-[15px] text-[#1F1A12] leading-relaxed">{risk}</p>
                  </div>
                </div>
              )}

              {reco && (
                <div className="flex gap-4 items-start" data-testid="compare-reco">
                  <div className="shrink-0 w-9 h-9 rounded-full bg-[#1F1A12] flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-[#F3D060]" />
                  </div>
                  <div className="flex-1 pt-1">
                    <div className="text-[10px] uppercase tracking-wider text-[#1F1A12] font-semibold mb-0.5">
                      Recommandation
                    </div>
                    <p className="text-[15px] text-[#1F1A12] leading-relaxed font-medium">{reco}</p>
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* =========== FOOTER ACTIONS =========== */}
          <footer className="flex flex-wrap gap-3 justify-end pt-6 border-t border-[#E5DFD0]">
            <button
              onClick={onClose}
              className="bg-white border border-[#6B5E48]/30 text-[#1F1A12] px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#FAF5EA] transition"
              data-testid="pulse-compare-close-footer"
            >
              Fermer
            </button>
            {currentBatch.report_paid && (
              <button
                onClick={downloadPdf}
                className="bg-[#1F1A12] text-white px-5 py-2.5 rounded-full text-sm font-medium hover:bg-[#2A2319] transition flex items-center gap-2"
                data-testid="pulse-compare-download-pdf"
              >
                <FileText className="w-4 h-4" /> Télécharger le rapport v2 (PDF)
              </button>
            )}
          </footer>
        </div>
      </div>
    </div>
  );
}

/* ===========================================================================
 * Helper: guess newcomer name from batches
 * ========================================================================== */
function guessNewcomerName(baselineBatch, currentBatch) {
  try {
    const baseEmails = new Set(baselineBatch.invited_emails || []);
    const curEmails = currentBatch.invited_emails || [];
    const fresh = curEmails.filter((e) => !baseEmails.has(e));
    if (fresh.length === 0) return 'le nouveau membre';
    const pmap = currentBatch.participants_map || {};
    const names = fresh.map((e) => pmap[e] || e.split('@')[0]);
    if (names.length === 1) return names[0];
    if (names.length === 2) return `${names[0]} et ${names[1]}`;
    return `${names.slice(0, -1).join(', ')} et ${names[names.length - 1]}`;
  } catch {
    return 'le nouveau membre';
  }
}
